import { memo, useState } from "react";
import type { BotezBokehTemplateProps, Translator } from "./botez-bokeh/types";
import { FONT_BODY, isRsvpCutoffPassed } from "./botez-bokeh/utils";
import { BotezBokehBackground } from "./botez-bokeh/BokehBackground";
import { BotezBokehNavbar } from "./botez-bokeh/Navbar";
import { BotezBokehHero } from "./botez-bokeh/Hero";
import { BotezBokehOurStory } from "./botez-bokeh/OurStory";
import { BotezBokehGallery } from "./botez-bokeh/Gallery";
import { BotezBokehFamilies } from "./botez-bokeh/Families";
import { BotezBokehDetails } from "./botez-bokeh/Details";
import { BotezBokehCountdown } from "./botez-bokeh/Countdown";
import { BotezBokehRSVP } from "./botez-bokeh/RSVP";
import { BotezBokehRegistry } from "./botez-bokeh/Registry";
import { BotezBokehSaveTheDate } from "./botez-bokeh/SaveTheDate";
import { BotezBokehFooter } from "./botez-bokeh/Footer";
import { PublicRsvpForm } from "@/components/public/PublicRsvpForm";

/**
 * Botez Bokeh template — baptism-first playful design (peachy/teal, bokeh
 * background, Pacifico script) adapted from the "Design 3 Jubiel (Botez)"
 * Figma prototype. Follows the ModernChicTemplate wrapper pattern.
 */
export const BotezBokehTemplate = memo((props: BotezBokehTemplateProps) => {
  const { event, config, locale, t: originalT } = props;
  const [rsvpDialogOpen, setRsvpDialogOpen] = useState(false);

  // Ensure t is always a function - provide fallback if undefined
  const t: Translator =
    typeof originalT === "function"
      ? originalT
      : (key: string, params?: Record<string, string | number | undefined>) => {
          const defaultValue = params?.defaultValue;
          return typeof defaultValue === "string" ? defaultValue : String(defaultValue ?? key);
        };

  const sectionProps = { ...props, t };
  const openRsvpDialog = () => setRsvpDialogOpen(true);

  return (
    <div
      className="relative flex flex-col w-full min-h-screen overflow-x-hidden transition-colors duration-500"
      style={{ background: config.palette.background, fontFamily: FONT_BODY }}
    >
      <BotezBokehBackground />
      <BotezBokehNavbar {...sectionProps} onRsvpClick={openRsvpDialog} />
      <BotezBokehHero {...sectionProps} onRsvpClick={openRsvpDialog} />
      <BotezBokehOurStory {...sectionProps} />
      <BotezBokehGallery {...sectionProps} />
      <BotezBokehFamilies {...sectionProps} />
      <BotezBokehDetails {...sectionProps} />
      <BotezBokehCountdown event={event} config={config} locale={locale} t={t} />
      <BotezBokehRSVP {...sectionProps} onRsvpClick={openRsvpDialog} />
      <BotezBokehRegistry {...sectionProps} />
      <BotezBokehSaveTheDate event={event} config={config} locale={locale} t={t} onShare={props.onShare} />
      <BotezBokehFooter event={event} config={config} locale={locale} t={t} />

      {/* RSVP Form Dialog (shared component) */}
      {event.rsvpSettings?.rsvpEnabled && !isRsvpCutoffPassed(event) && (
        <PublicRsvpForm
          event={event}
          config={config}
          locale={locale}
          open={rsvpDialogOpen}
          onOpenChange={setRsvpDialogOpen}
        />
      )}
    </div>
  );
});

BotezBokehTemplate.displayName = "BotezBokehTemplate";
